import { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { clsx } from 'clsx';
import { sendRemoveDeviceRequest } from '../../../utils/api/accountApi';
import { type UserProfile } from '../../../utils/api/schema/profileSchema';
import { Button } from '../../common/Button';
import { sendDeleteRouterConfigurationRequest } from '../../../utils/api/routerAPI';
import { maxDeviceLimit } from '../../../utils/consts/profileConsts';
import UnlinkDeviceModal from './components/UnlinkDeviceModal';
import AndroidIcon from '../../../../assets/icons/platforms/Android.svg';
import iOSIcon from '../../../../assets/icons/platforms/iOS.svg';
import MacOSIcon from '../../../../assets/icons/platforms/macOS.png';
import WindowsIcon from '../../../../assets/icons/platforms/Windows.svg';
import RouterIcon from '../../../../assets/icons/platforms/router.svg';
import iPadIcon from '../../../../assets/icons/platforms/iPad.png';
import AppleTVIcon from '../../../../assets/icons/platforms/apple-tv.svg';
import AndroidTVIcon from '../../../../assets/icons/platforms/android-tv.svg';
import AddDeviceIcon from '../../../../assets/icons/add-device-icon.svg';
import styles from './DeviceManagement.scss';

const getPlatformIcon = (platform: UserProfile['devices'][number]['platform'], deviceName: string) => {
  const p = platform.toLowerCase();

  switch (p) {
    case 'android': {
      return AndroidIcon;
    }
    case 'ios': {
      if (deviceName.toLowerCase().includes('ipad')) {
        return iPadIcon;
      }

      return iOSIcon;
    }
    case 'macos': {
      return MacOSIcon;
    }
    case 'windows': {
      return WindowsIcon;
    }
    case 'router': {
      return RouterIcon;
    }
    case 'apple-tv': {
      return AppleTVIcon;
    }
    case 'android-tv': {
      return AndroidTVIcon;
    }

    default: {
      return '';
    }
  }
};

type ProfileDevice = UserProfile['devices'][number];

interface DeviceManagementProps {
  devices: ProfileDevice[];
  isSetappSubscription: boolean;
  onAddNewDeviceClick: () => void;
  onDeviceRemoved: (removedDeviceId: string) => void;
  onRouterConfigureClick: (routerId: string, routerName: string) => void;
}

const DeviceManagementBlock = ({
  devices,
  isSetappSubscription,
  onAddNewDeviceClick,
  onDeviceRemoved,
  onRouterConfigureClick,
}: DeviceManagementProps) => {
  const intl = useIntl();

  const [isUnlinkDialogVisible, setIsUnlinkDialogVisible] = useState(false);
  const [unlinkError, setUnlinkError] = useState('');
  const [deletedDevice, setDeletedDevice] = useState<ProfileDevice | null>(null);

  const [maxDevicesPerAccount, setMaxDevicesPerAccount] = useState(
    isSetappSubscription ? maxDeviceLimit.SETAPP : maxDeviceLimit.DEFAULT,
  );

  useEffect(() => {
    setMaxDevicesPerAccount(isSetappSubscription ? maxDeviceLimit.SETAPP : maxDeviceLimit.DEFAULT);
  }, [isSetappSubscription]);

  const onUnlinkDeviceClick = (device: ProfileDevice) => {
    setDeletedDevice(device);
    setIsUnlinkDialogVisible(true);
  };

  const unlinkDevice = (id?: string) => {
    if (!id) {
      return Promise.reject(new Error('No device id provided'));
    }
    const device = devices.find((d) => d.id === id);

    if (!device) {
      return Promise.reject(new Error('Device not found'));
    }

    return Promise.resolve()
      .then(() =>
        device.platform === 'router' ? sendDeleteRouterConfigurationRequest(id) : sendRemoveDeviceRequest(id),
      )
      .then(() => {
        setIsUnlinkDialogVisible(false);
        setUnlinkError('');
        setDeletedDevice(null);
        onDeviceRemoved(device.id);
      })
      .catch((error: unknown) => {
        setUnlinkError(
          intl.formatMessage({
            id: 'profile.deviceManagement.error.deviceUnlinking',
            defaultMessage: 'Unlinking failed, please try again later',
            description: 'Error message shown on a failed attempt to remove device from the account',
          }),
        );
        throw error;
      });
  };
  const onCancelUnlink = (id?: string) => {
    if (!id) {
      return;
    }

    setIsUnlinkDialogVisible(false);
  };

  return (
    <section className={styles.section}>
      {devices.map((device) => (
        <div className={styles.item} key={device.id}>
          <div className={styles.device}>
            <img src={getPlatformIcon(device.platform, device.name)} className={styles.itemImage} alt="" />

            <div className={styles.deviceTitle}>{device.name}</div>

            <div className={styles.unlinkLine}>
              {device.platform === 'router' && (
                <Button
                  className={styles.configureButton}
                  kind="white"
                  buttonSize="small"
                  onClick={() => {
                    onRouterConfigureClick(device.id, device.name);
                  }}
                >
                  <FormattedMessage
                    id="profile.deviceManagement.configureRouterButton"
                    defaultMessage="Configurations"
                    description="Button to update selected router from the account"
                  />
                </Button>
              )}
              <Button
                className={styles.unlinkButton}
                kind="white"
                buttonSize="small"
                onClick={() => {
                  onUnlinkDeviceClick(device);
                }}
              >
                {device.platform === 'router' ? (
                  <FormattedMessage
                    id="profile.deviceManagement.deleteRouterButton"
                    defaultMessage="Delete"
                    description="Button to remove selected router from the account"
                  />
                ) : (
                  <FormattedMessage
                    id="profile.deviceManagement.unpairButton"
                    defaultMessage="Unpair"
                    description="Button to remove selected device from the account"
                  />
                )}
              </Button>
            </div>
          </div>
        </div>
      ))}

      {devices.length < maxDevicesPerAccount && (
        <button
          type="button"
          className={clsx(styles.item, styles.addNewDevice, devices.length === 0 && styles.addNewItem)}
          onClick={onAddNewDeviceClick}
        >
          <div className={styles.device}>
            <img src={AddDeviceIcon} width="24" height="24" alt="" />
            <span>
              <FormattedMessage
                id="profile.deviceManagement.addNewDeviceButton"
                defaultMessage="Add new device"
                description="Button to add a device to the account"
              />
            </span>
          </div>
        </button>
      )}

      <UnlinkDeviceModal
        visible={isUnlinkDialogVisible}
        error={unlinkError}
        isRouter={deletedDevice?.platform === 'router'}
        onUnlink={() => unlinkDevice(deletedDevice?.id)}
        onCancel={() => {
          onCancelUnlink(deletedDevice?.id);
        }}
      />
    </section>
  );
};

export default DeviceManagementBlock;
